/**
 * Enhanced Tracking Service with Data Merging Integration
 * Extends the existing tracking service with intelligent data merging capabilities
 */

import { 
  getEnhancedMessageTrackingData as getBaseTrackingData,
  refreshCampaignTrackingEnhanced,
  transformEventsToEnhancedTrackingData,
  getMailgunMessageEvents,
  getMailgunMessageDetails,
} from './tracking';
import { 
  mergeTrackingEvents, 
  validateTrackingDataIntegrity,
  type MergeResult,
  type MergeOptions 
} from '../../../utils/dataMerging';
import { validateTrackingData } from '../../../utils/dataValidation';
import type { 
  EnhancedMessageTrackingData, 
  CampaignStats,
  MailgunEvent 
} from '../types';

// Enhanced service configuration
const ENHANCED_SERVICE_CONFIG = {
  // Cache duration for merged data (in ms)
  MERGED_DATA_CACHE_DURATION: 5 * 60 * 1000, // 5 minutes
  // Maximum number of merge operations to track
  MAX_MERGE_HISTORY: 50,
  // Validation threshold for data quality
  VALIDATION_ERROR_THRESHOLD: 0.1, // 10% error rate threshold
} as const;

// In-memory cache for merged data
const mergedDataCache = new Map<string, {
  data: EnhancedMessageTrackingData[];
  timestamp: number;
  mergeMetadata: {
    totalMerges: number;
    lastMergeConflicts: number;
    dataQualityScore: number;
  };
}>();

// Merge operation history
const mergeHistory: Array<{
  campaignId: string;
  timestamp: number;
  operation: 'merge' | 'refresh' | 'validate';
  itemsProcessed: number;
  conflicts: number;
  duration: number;
}> = [];

/**
 * Get enhanced message tracking data with intelligent merging
 * This function combines fresh API data with cached data using smart merge strategies
 */
export const getEnhancedMessageTrackingDataWithMerging = async (
  campaignId: string,
  options: {
    forceRefresh?: boolean;
    mergeOptions?: Partial<MergeOptions>;
    enableValidation?: boolean;
    maxCacheAge?: number;
  } = {}
): Promise<{
  data: EnhancedMessageTrackingData[];
  mergeResult?: MergeResult<EnhancedMessageTrackingData[]>;
  validationResult?: any;
  fromCache: boolean;
}> => {
  const startTime = performance.now();
  const {
    forceRefresh = false,
    mergeOptions = {},
    enableValidation = true,
    maxCacheAge = ENHANCED_SERVICE_CONFIG.MERGED_DATA_CACHE_DURATION,
  } = options;

  try {
    console.log(`Getting enhanced tracking data with merging for campaign: ${campaignId}`);

    // Early return for invalid campaign IDs to prevent API calls
    if (!campaignId || campaignId === 'default' || campaignId.length < 10) {
      console.log(`Skipping API call for invalid campaign ID: ${campaignId}`);
      return {
        data: [],
        fromCache: false,
      };
    }

    // Check cache first (unless force refresh is requested)
    const cachedEntry = mergedDataCache.get(campaignId);
    const now = Date.now();
    
    if (!forceRefresh && cachedEntry && (now - cachedEntry.timestamp) < maxCacheAge) {
      console.log(`Returning cached merged data for campaign ${campaignId}`);
      return {
        data: cachedEntry.data,
        fromCache: true,
      };
    }

    // Get fresh data from API
    console.log(`Fetching fresh data for campaign ${campaignId}`);
    const freshData = await refreshCampaignTrackingEnhanced(campaignId);

    let mergeResult: MergeResult<EnhancedMessageTrackingData[]> | undefined;
    let finalData = freshData;

    // If we have cached data, merge it with fresh data
    if (cachedEntry && cachedEntry.data.length > 0) {
      console.log(`Merging fresh data with cached data for campaign ${campaignId}`);
      
      const defaultMergeOptions: Partial<MergeOptions> = {
        timestampStrategy: 'latest',
        validateIntegrity: true,
        deepMerge: true,
        ...mergeOptions,
      };

      mergeResult = mergeTrackingEvents(cachedEntry.data, freshData, defaultMergeOptions);
      finalData = mergeResult.data;

      console.log(`Merge completed: ${mergeResult.changesSummary.added} added, ${mergeResult.changesSummary.updated} updated, ${mergeResult.conflicts?.length || 0} conflicts`);
    }

    // Validate data if requested
    let validationResult;
    if (enableValidation) {
      validationResult = validateTrackingData(finalData);
      
      if (!validationResult.isValid) {
        console.warn(`Data validation failed for campaign ${campaignId}:`, validationResult.summary);
        
        // If validation fails severely, fall back to fresh data only
        const errorRate = validationResult.summary.errorCount / validationResult.summary.totalItems;
        if (errorRate > ENHANCED_SERVICE_CONFIG.VALIDATION_ERROR_THRESHOLD) {
          console.warn(`Error rate ${(errorRate * 100).toFixed(1)}% exceeds threshold, using fresh data only`);
          finalData = freshData;
          mergeResult = undefined;
        }
      }
    }

    // Calculate data quality score
    const dataQualityScore = validationResult 
      ? Math.max(0, 1 - (validationResult.summary.errorCount / Math.max(1, validationResult.summary.totalItems)))
      : 1;

    // Update cache
    mergedDataCache.set(campaignId, {
      data: finalData,
      timestamp: now,
      mergeMetadata: {
        totalMerges: (cachedEntry?.mergeMetadata.totalMerges || 0) + (mergeResult ? 1 : 0),
        lastMergeConflicts: mergeResult?.conflicts?.length || 0,
        dataQualityScore,
      },
    });

    // Record merge operation in history
    const duration = performance.now() - startTime;
    recordMergeOperation({
      campaignId,
      timestamp: now,
      operation: mergeResult ? 'merge' : 'refresh',
      itemsProcessed: finalData.length,
      conflicts: mergeResult?.conflicts?.length || 0,
      duration,
    });

    console.log(`Enhanced tracking data retrieval completed for ${campaignId} in ${duration.toFixed(2)}ms`);

    return {
      data: finalData,
      mergeResult,
      validationResult,
      fromCache: false,
    };

  } catch (error) {
    console.error(`Error getting enhanced tracking data with merging for ${campaignId}:`, error);
    
    // Handle CORS and network errors gracefully
    if (error instanceof Error) {
      if (error.message.includes('CORS') || error.message.includes('Network Error') || 
          error.message.includes('Failed to fetch') || error.name === 'TypeError') {
        console.warn(`Network/CORS error detected for campaign ${campaignId}, returning empty data`);
        return {
          data: [],
          fromCache: false,
        };
      }
    }
    
    // Try to return cached data as fallback
    const cachedEntry = mergedDataCache.get(campaignId);
    if (cachedEntry) {
      console.warn(`Returning stale cached data due to error for campaign ${campaignId}`);
      return {
        data: cachedEntry.data,
        fromCache: true,
      };
    }

    // If no cache available, return empty data instead of throwing
    console.warn(`No cached data available for campaign ${campaignId}, returning empty data`);
    return {
      data: [],
      fromCache: false,
    };
  }
};

/**
 * Incrementally update tracking data with new events
 * This function efficiently merges new events without full data refresh
 */
export const updateTrackingDataWithNewEvents = async (
  campaignId: string,
  newEvents: MailgunEvent[],
  options: {
    mergeOptions?: Partial<MergeOptions>;
    enableValidation?: boolean;
  } = {}
): Promise<MergeResult<EnhancedMessageTrackingData[]>> => {
  const startTime = performance.now();
  const { mergeOptions = {}, enableValidation = true } = options;

  try {
    console.log(`Updating tracking data with ${newEvents.length} new events for campaign ${campaignId}`);

    // Get current cached data
    const cachedEntry = mergedDataCache.get(campaignId);
    if (!cachedEntry) {
      throw new Error(`No cached data found for campaign ${campaignId}. Use getEnhancedMessageTrackingDataWithMerging first.`);
    }

    // Group events by message ID
    const eventsByMessageId = new Map<string, MailgunEvent[]>();
    newEvents.forEach(event => {
      const messageId = event.message?.headers?.['message-id'];
      if (messageId) {
        if (!eventsByMessageId.has(messageId)) {
          eventsByMessageId.set(messageId, []);
        }
        eventsByMessageId.get(messageId)!.push(event);
      }
    });

    // Transform new events to tracking data
    const newTrackingData: EnhancedMessageTrackingData[] = [];
    for (const [messageId, events] of Array.from(eventsByMessageId)) {
      try {
        // Try to get message details for subject
        let subject = 'Unknown Subject';
        try {
          const messageDetails = await getMailgunMessageDetails(messageId);
          subject = messageDetails.subject || subject;
        } catch (error) {
          console.warn(`Could not fetch message details for ${messageId}, using default subject`);
        }

        const trackingData = transformEventsToEnhancedTrackingData(events, messageId, subject);
        newTrackingData.push(trackingData);
      } catch (error) {
        console.error(`Error transforming events for message ${messageId}:`, error);
      }
    }

    // Merge with existing data
    const defaultMergeOptions: Partial<MergeOptions> = {
      timestampStrategy: 'latest',
      validateIntegrity: true,
      deepMerge: true,
      ...mergeOptions,
    };

    const mergeResult = mergeTrackingEvents(cachedEntry.data, newTrackingData, defaultMergeOptions);

    // Validate merged data if requested
    if (enableValidation) {
      const validationResult = validateTrackingData(mergeResult.data);
      if (!validationResult.isValid) {
        console.warn(`Validation failed after incremental update for campaign ${campaignId}:`, validationResult.summary);
      }
    }

    // Update cache
    const now = Date.now();
    mergedDataCache.set(campaignId, {
      data: mergeResult.data,
      timestamp: now,
      mergeMetadata: {
        totalMerges: cachedEntry.mergeMetadata.totalMerges + 1,
        lastMergeConflicts: mergeResult.conflicts?.length || 0,
        dataQualityScore: cachedEntry.mergeMetadata.dataQualityScore, // Keep previous score for incremental updates
      },
    });

    // Record operation
    const duration = performance.now() - startTime;
    recordMergeOperation({
      campaignId,
      timestamp: now,
      operation: 'merge',
      itemsProcessed: newTrackingData.length,
      conflicts: mergeResult.conflicts?.length || 0,
      duration,
    });

    console.log(`Incremental update completed for ${campaignId}: ${mergeResult.changesSummary.added} added, ${mergeResult.changesSummary.updated} updated`);

    return mergeResult;

  } catch (error) {
    console.error(`Error updating tracking data with new events for ${campaignId}:`, error);
    throw error;
  }
};

/**
 * Batch update multiple campaigns with optimized merging
 */
export const batchUpdateCampaignTrackingData = async (
  campaignUpdates: Array<{
    campaignId: string;
    forceRefresh?: boolean;
    mergeOptions?: Partial<MergeOptions>;
  }>,
  options: {
    maxConcurrency?: number;
    enableValidation?: boolean;
  } = {}
): Promise<Array<{
  campaignId: string;
  success: boolean;
  data?: EnhancedMessageTrackingData[];
  error?: string;
  mergeResult?: MergeResult<EnhancedMessageTrackingData[]>;
}>> => {
  const { maxConcurrency = 3, enableValidation = true } = options;
  const results: Array<{
    campaignId: string;
    success: boolean;
    data?: EnhancedMessageTrackingData[];
    error?: string;
    mergeResult?: MergeResult<EnhancedMessageTrackingData[]>;
  }> = [];

  console.log(`Starting batch update for ${campaignUpdates.length} campaigns with max concurrency ${maxConcurrency}`);

  // Process campaigns in batches to control concurrency
  for (let i = 0; i < campaignUpdates.length; i += maxConcurrency) {
    const batch = campaignUpdates.slice(i, i + maxConcurrency);
    
    const batchPromises = batch.map(async (update) => {
      try {
        const result = await getEnhancedMessageTrackingDataWithMerging(
          update.campaignId,
          {
            forceRefresh: update.forceRefresh,
            mergeOptions: update.mergeOptions,
            enableValidation,
          }
        );

        return {
          campaignId: update.campaignId,
          success: true,
          data: result.data,
          mergeResult: result.mergeResult,
        };
      } catch (error) {
        console.error(`Error updating campaign ${update.campaignId}:`, error);
        return {
          campaignId: update.campaignId,
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        };
      }
    });

    const batchResults = await Promise.all(batchPromises);
    results.push(...batchResults);

    console.log(`Completed batch ${Math.floor(i / maxConcurrency) + 1}/${Math.ceil(campaignUpdates.length / maxConcurrency)}`);
  }

  const successCount = results.filter(r => r.success).length;
  console.log(`Batch update completed: ${successCount}/${results.length} campaigns updated successfully`);

  return results;
};

/**
 * Get merge statistics for a campaign
 */
export const getCampaignMergeStatistics = (campaignId: string) => {
  const cachedEntry = mergedDataCache.get(campaignId);
  const campaignHistory = mergeHistory.filter(h => h.campaignId === campaignId);

  return {
    cached: !!cachedEntry,
    cacheAge: cachedEntry ? Date.now() - cachedEntry.timestamp : null,
    mergeMetadata: cachedEntry?.mergeMetadata,
    operationHistory: campaignHistory.slice(-10), // Last 10 operations
    totalOperations: campaignHistory.length,
    averageDuration: campaignHistory.length > 0 
      ? campaignHistory.reduce((sum, op) => sum + op.duration, 0) / campaignHistory.length 
      : 0,
  };
};

/**
 * Get global merge statistics across all campaigns
 */
export const getGlobalMergeStatistics = () => {
  const totalCachedCampaigns = mergedDataCache.size;
  const totalOperations = mergeHistory.length;
  const recentOperations = mergeHistory.slice(-20); // Last 20 operations

  const operationsByType = mergeHistory.reduce((acc, op) => {
    acc[op.operation] = (acc[op.operation] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const averageDuration = totalOperations > 0 
    ? mergeHistory.reduce((sum, op) => sum + op.duration, 0) / totalOperations 
    : 0;

  const totalConflicts = mergeHistory.reduce((sum, op) => sum + op.conflicts, 0);

  return {
    totalCachedCampaigns,
    totalOperations,
    operationsByType,
    averageDuration,
    totalConflicts,
    recentOperations,
    cacheHitRate: calculateCacheHitRate(),
  };
};

/**
 * Clear cache for a specific campaign or all campaigns
 */
export const clearMergeCache = (campaignId?: string) => {
  if (campaignId) {
    const deleted = mergedDataCache.delete(campaignId);
    console.log(`Cache cleared for campaign ${campaignId}: ${deleted ? 'success' : 'not found'}`);
  } else {
    const size = mergedDataCache.size;
    mergedDataCache.clear();
    console.log(`Cleared cache for ${size} campaigns`);
  }
};

/**
 * Optimize cache by removing old entries
 */
export const optimizeMergeCache = (maxAge: number = ENHANCED_SERVICE_CONFIG.MERGED_DATA_CACHE_DURATION) => {
  const now = Date.now();
  let removedCount = 0;

  for (const [campaignId, entry] of Array.from(mergedDataCache.entries())) {
    if (now - entry.timestamp > maxAge) {
      mergedDataCache.delete(campaignId);
      removedCount++;
    }
  }

  console.log(`Cache optimization completed: removed ${removedCount} expired entries`);
  return removedCount;
};

// Helper functions

/**
 * Record a merge operation in history
 */
const recordMergeOperation = (operation: {
  campaignId: string;
  timestamp: number;
  operation: 'merge' | 'refresh' | 'validate';
  itemsProcessed: number;
  conflicts: number;
  duration: number;
}) => {
  mergeHistory.push(operation);
  
  // Keep history size manageable
  if (mergeHistory.length > ENHANCED_SERVICE_CONFIG.MAX_MERGE_HISTORY) {
    mergeHistory.splice(0, mergeHistory.length - ENHANCED_SERVICE_CONFIG.MAX_MERGE_HISTORY);
  }
};

/**
 * Calculate cache hit rate based on recent operations
 */
const calculateCacheHitRate = (): number => {
  const recentOperations = mergeHistory.slice(-50); // Last 50 operations
  if (recentOperations.length === 0) return 0;

  const cacheHits = recentOperations.filter(op => op.operation === 'merge').length;
  return cacheHits / recentOperations.length;
};

// Main export alias
export { getEnhancedMessageTrackingDataWithMerging as getEnhancedMessageTrackingData };

// Re-export base functions for compatibility
export { getEnhancedMessageTrackingData as getEnhancedMessageTrackingDataBase } from './tracking';